import { CheckBoxWithLabelWidget, CompactInlineAlertWidget, InlineAlertType,TabStyles,TextAreaWidget,TextFieldWidget } from '@tableau/widgets';
import React from 'react';

import { Messages } from '~src/codegen/Localize';
import { FlowOutputStepInfo } from '~src/flow/models/FlowOutputStepInfo';
import { OutputFileFormat } from '~src/flow/serverApi/types/SubscriptionFlow';
import { cssClass } from '~src/style';

import { GroupRecipient, Recipient, UserRecipient } from '../types/Recipient';
import { FlowSubscriptionOutputsSection } from './FlowSubscriptionOutputsSection';
import { RecipientPicker } from './RecipientPicker';


const SUBJECT_MAX_LENGTH: number = 256;
const MESSAGE_MAX_LENGTH: number = 2000;
const ROOT_CLASS: string = 'FlowSubscriptionUberContent';

const titleClass = cssClass(ROOT_CLASS, 'title', {
  display: 'block',
  paddingBottom: TabStyles.Spacing.MediumSpace,
});

const inputClass = cssClass(ROOT_CLASS, 'input', {
  width: '100%',
  paddingTop: TabStyles.Spacing.MediumSpace,
});

const textAreaClass = cssClass(ROOT_CLASS, 'textarea', {
  width: '100%',
  paddingTop: TabStyles.Spacing.MediumSpace,
});

const alertWrapper = cssClass(ROOT_CLASS, 'alertWrapper', {
  paddingBottom: TabStyles.Spacing.DefaultSpace,
});

export interface FlowSubscriptionDialogContentProps {
  readonly hideInfoMessage?: boolean;
  readonly outputs: ReadonlyArray<FlowOutputStepInfo>;
  readonly shouldSendToMe: boolean;
  readonly shouldIncludeOutputData: boolean;
  readonly subjectValue: string;
  readonly messageValue: string;
  readonly shouldShowFirstNRows: boolean;
  readonly shouldAttachOutputDataToEmail: boolean;
  readonly shouldIncludeLinkToDatasource: boolean;
  readonly outputFileFormat: OutputFileFormat;
  readonly selectedOutputForSubscription: string | null;
  readonly emailRecipients: Recipient[];
  readonly hideRecipientsAlert?: boolean;
  readonly onShowFirstNRowsToggled: () => void;
  readonly onAttachOutputDataToEmailToggled: () => void;
  readonly onShouldSendToMeToggled: () => void;
  readonly onSubjectChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  readonly onMessageChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  readonly onRecipientsChange: (recipients: Recipient[]) => void;
  readonly onShouldIncludeOutputDataToggled: () => void;
  readonly onIncludeLinkToDatasourceToggled: () => void;
  readonly onOutputFileFormatChange: (value: string) => void;
  readonly onSelectedOutputForSubscriptionChange: (value: string) => void;
}

export const FlowSubscriptionDialogContent: React.FC<FlowSubscriptionDialogContentProps>  = (props: FlowSubscriptionDialogContentProps) => {

  return (
    <>
      {!props.hideInfoMessage && <span className={titleClass} data-tb-test-id={'info-message-test-id'}>{Messages.flowSubscriptionsAddFlowSubscription()}</span>}
      {!props.hideRecipientsAlert && !props.shouldSendToMe && props.emailRecipients.length < 1 &&
        <div className={alertWrapper}>
          <CompactInlineAlertWidget
            testId={'no-recipients-alert-test-id'}
            type={InlineAlertType.Error}
          >
            {Messages.flowSubscriptionsNoRecipients()}
          </CompactInlineAlertWidget>
        </div>
      }
      <RecipientPicker
        label={Messages.subscriptionRecipiantPickerLabel()}
        placeholder={Messages.flowSubscriptionsRecipientPickerPlaceholder()}
        onRecipientsChanged={props.onRecipientsChange}
        initialUsers={props.emailRecipients.filter((recipient): recipient is UserRecipient => recipient.kind === 'USER')}
        initialGroups={props.emailRecipients.filter((recipient): recipient is GroupRecipient => recipient.kind === 'GROUP')}
      />
      <CheckBoxWithLabelWidget
        label={Messages.flowSubscriptionsSendToMe()}
        checked={props.shouldSendToMe}
        handleChange={props.onShouldSendToMeToggled}
        testId={'flow-subscription-send-to-me'}
        style={{marginTop: TabStyles.Spacing.DefaultSpace, marginLeft: 0}}
      />
      <TextFieldWidget
        testId='flow-subscription-subject'
        label={Messages.flowSubscriptionsSubject()}
        kind={'line'}
        maxLength={SUBJECT_MAX_LENGTH}
        onChange={props.onSubjectChange}
        value={props.subjectValue}
        valid={props.subjectValue.length > SUBJECT_MAX_LENGTH || props.subjectValue === '' ? false : undefined}
        style={{width: '100%'}}
        className={inputClass}
      />
      <TextAreaWidget
        label={Messages.flowSubscriptionsEmailMessage()}
        testId={'flow-subscription-message'}
        rows={2}
        onChange={props.onMessageChange}
        value={props.messageValue}
        maxLength={MESSAGE_MAX_LENGTH}
        className={textAreaClass}
        valid={props.messageValue.length > MESSAGE_MAX_LENGTH ? false : undefined}
        style={{width: '100%'}}
      />
      <FlowSubscriptionOutputsSection
        outputs={props.outputs}
        shouldIncludeOutputData={props.shouldIncludeOutputData}
        shouldAttachOutputDataToEmail={props.shouldAttachOutputDataToEmail}
        shouldShowFirstNRows={props.shouldShowFirstNRows}
        shouldIncludeLinkToDatasource={props.shouldIncludeLinkToDatasource}
        outputFileFormat={props.outputFileFormat}
        selectedOutputForSubscription={props.selectedOutputForSubscription}
        onIncludeOutputDataToggled={props.onShouldIncludeOutputDataToggled}
        onAttachOutputDataToEmailToggled={props.onAttachOutputDataToEmailToggled}
        onShowFirstNRowsToggled={props.onShowFirstNRowsToggled}
        onIncludeLinkToDatasourceToggled={props.onIncludeLinkToDatasourceToggled}
        onOutputFileFormatChange={props.onOutputFileFormatChange}
        onSelectedOutputForSubscriptionChange={props.onSelectedOutputForSubscriptionChange}
      />
    </>
  );
};

FlowSubscriptionDialogContent.displayName = 'FlowSubscriptionDialogContent';
